/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetGcpKmsKeyRequest,
  GetGcpKmsKeySecurity,
} from "../models/operations/getgcpkmskey.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetGcpKmsKeyQuery,
  GetGcpKmsKeyQueryData,
  prefetchGetGcpKmsKey,
  queryKeyGetGcpKmsKey,
} from "./getGcpKmsKey.core.js";
export {
  buildGetGcpKmsKeyQuery,
  type GetGcpKmsKeyQueryData,
  prefetchGetGcpKmsKey,
  queryKeyGetGcpKmsKey,
};

export type GetGcpKmsKeyQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getGcpKmsKey externalKeys
 *
 * @remarks
 * Get a GCP KMS external key by ID. Requires org:read.
 */
export function useGetGcpKmsKey(
  request: GetGcpKmsKeyRequest,
  security?: GetGcpKmsKeySecurity | undefined,
  options?: QueryHookOptions<GetGcpKmsKeyQueryData, GetGcpKmsKeyQueryError>,
): UseQueryResult<GetGcpKmsKeyQueryData, GetGcpKmsKeyQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetGcpKmsKeyQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getGcpKmsKey externalKeys
 *
 * @remarks
 * Get a GCP KMS external key by ID. Requires org:read.
 */
export function useGetGcpKmsKeySuspense(
  request: GetGcpKmsKeyRequest,
  security?: GetGcpKmsKeySecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetGcpKmsKeyQueryData,
    GetGcpKmsKeyQueryError
  >,
): UseSuspenseQueryResult<GetGcpKmsKeyQueryData, GetGcpKmsKeyQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetGcpKmsKeyQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetGcpKmsKeyData(
  client: QueryClient,
  queryKeyBase: [parameters: { id: string; gramSession?: string | undefined }],
  data: GetGcpKmsKeyQueryData,
): GetGcpKmsKeyQueryData | undefined {
  const key = queryKeyGetGcpKmsKey(...queryKeyBase);

  return client.setQueryData<GetGcpKmsKeyQueryData>(key, data);
}

export function invalidateGetGcpKmsKey(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { id: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "externalKeys", "getGcpKms", ...queryKeyBase],
  });
}

export function invalidateAllGetGcpKmsKey(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "externalKeys", "getGcpKms"],
  });
}
